import { contractMakerRouteFromLocation } from "@dashboard/routes/dashboard-navigation"
import { dashboardAppHostRedirectUrl } from "@playbrain/config/dashboard-host"
import { currentRequestHostname } from "@playbrain/config/dashboard-host-runtime"
import type { QueryClient } from "@tanstack/react-query"
import {
  listContractMakerLibraryCategoriesQueryOptions,
  listContractMakerTemplatesQueryOptions,
} from "./contract-maker-queries"

/**
 * What both `/dashboard/contract-maker/*` routes need.
 *
 * Two deployments serve that URL: the site bundle mounts it inside the whole
 * dashboard shell, and the Contract Maker bundle mounts the feature on its own.
 * The mounting is genuinely different, so the two route files stay — but the
 * host redirect and the library prefetch are the same work either way, and
 * having them written twice is how they came apart: the site route grew a
 * loader and the standalone one did not, so the deployment that exists to serve
 * this feature alone was the one loading it serially.
 */

/**
 * The dashboard session cookie is host-only, set on the dashboard host. Reached
 * on the public host the visitor has no cookie, so they look signed out and
 * their API calls are refused by the origin guard. Returns the href to send
 * them to instead, or null when they are already on the right host.
 */
export async function contractMakerHostRedirect(href: string): Promise<string | null> {
  return dashboardAppHostRedirectUrl(await currentRequestHostname(), href) ?? null
}

/**
 * Warms the template library.
 *
 * Only the library is worth prefetching: the builder and the admin editor open
 * one template by an id the route knows nothing about until the path is parsed,
 * and they fetch it themselves.
 *
 * Never rejects. A prefetch that fails should cost the visitor a slower first
 * paint, not the route.
 */
export async function prefetchContractMakerLibrary(
  queryClient: QueryClient,
  location: { pathname: string; searchStr: string }
): Promise<void> {
  const contractRoute = contractMakerRouteFromLocation({
    pathname: location.pathname,
    search: location.searchStr,
  })
  if (contractRoute.type !== "library") return

  await Promise.all([
    queryClient.ensureQueryData(listContractMakerTemplatesQueryOptions()),
    queryClient.ensureQueryData(listContractMakerLibraryCategoriesQueryOptions()),
  ]).catch(() => undefined)
}
